import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { downloadTrackWithWatermark } from 'app/apis/track';
import { toastError, toastSuccess } from 'app/helpers/toast';
import { useTrackSlice } from 'app/pages/Tracks/slice';
import { selectSliceTracks } from 'app/pages/Tracks/slice/selectors';

/**
 * Component xử lý download queue tự động
 * - Lấy track tiếp theo từ queue khi không có track nào đang download
 * - Thực hiện download track với watermark
 * - Cập nhật state khi download hoàn thành hoặc lỗi
 */
export default function DownloadQueueProcessor() {
  const { actions } = useTrackSlice();
  const dispatch = useDispatch();
  const { downloadQueue, downloadingTrackId, downloadingTrack } =
    useSelector(selectSliceTracks);

  // Tự động dequeue track tiếp theo khi queue có item và không có track nào đang download
  useEffect(() => {
    if (!downloadingTrackId && (downloadQueue || []).length > 0) {
      dispatch(actions.dequeueTrackDownload());
    }
  }, [actions, dispatch, downloadQueue, downloadingTrackId]);

  // Xử lý download track hiện tại
  useEffect(() => {
    if (!downloadingTrack) return;

    let cancelled = false;

    const processDownload = async () => {
      const { trackId, artist, title, type, numberDownloads } =
        downloadingTrack;

      try {
        // Gọi API download với watermark
        const { downloadUrl, watermarkInfo } = await downloadTrackWithWatermark(
          trackId,
        );

        if (cancelled) return;

        // Tạo link download và trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;

        const apiFilename = watermarkInfo?.filename;
        const extension = type === 'audio' ? 'mp3' : 'mp4';
        const fallbackFilename = `${artist || 'Unknown'} - ${
          title || 'Track'
        }.${extension}`;
        link.setAttribute('download', apiFilename || fallbackFilename);

        document.body.appendChild(link);
        link.click();
        link.remove();

        // Cleanup blob URL
        window.URL.revokeObjectURL(downloadUrl);

        // Cập nhật số lượng download còn lại
        if (typeof numberDownloads === 'number') {
          dispatch(
            actions.updateNumberDownloadTrackSuccess({
              trackId,
              numberDownloads: numberDownloads - 1,
            }),
          );
        }

        // Đánh dấu download hoàn thành
        dispatch(actions.finishTrackDownload({ trackId }));
        toastSuccess('Download success!');
      } catch (error: any) {
        if (cancelled) return;

        // Xử lý lỗi
        const statusCode = error?.response?.data?.statusCode;
        const message = error?.response?.data?.message;

        if (statusCode === 5401) {
          toastError(
            message || 'You do not have access to download this track',
          );
        } else {
          toastError('Download failed. Please try again.');
        }

        // Đánh dấu download lỗi
        dispatch(
          actions.failTrackDownload({ trackId: downloadingTrack.trackId }),
        );
      }
    };

    processDownload();

    // Cleanup function để cancel download khi component unmount
    return () => {
      cancelled = true;
    };
  }, [actions, dispatch, downloadingTrack]);

  // Component này không render gì
  return null;
}
